import type {
  AppConfig,
  DailyCalculation,
  DailyPayload,
  IntegrityIssue,
  ShiftCalculation,
  ShiftLine,
} from "./domain";

const roundYen = (value: number, mode: AppConfig["wageRoundingMode"]) => {
  if (mode === "FLOOR") return Math.floor(value);
  if (mode === "CEIL") return Math.ceil(value);
  return Math.round(value);
};

const isLateNightMinute = (date: Date) => {
  const utcMinute = Math.floor(date.getTime() / 60_000);
  const japanMinuteOfDay = ((utcMinute + 9 * 60) % 1_440 + 1_440) % 1_440;
  return japanMinuteOfDay >= 22 * 60 || japanMinuteOfDay < 5 * 60;
};

export function calculateShift(
  shift: ShiftLine,
  config: Pick<AppConfig, "staff" | "lateNightPremiumRate" | "wageRoundingMode">,
): ShiftCalculation {
  const startedAt = new Date(shift.startedAt);
  const endedAt = new Date(shift.endedAt);
  const rawElapsed = Math.round((endedAt.getTime() - startedAt.getTime()) / 60_000);

  if (!Number.isFinite(rawElapsed) || rawElapsed <= 0 || rawElapsed >= 1_440) {
    throw new Error("勤務時間は0分より長く24時間未満にしてください");
  }
  if (shift.breakMinutes < 0 || shift.breakMinutes >= rawElapsed) {
    throw new Error("休憩時間は勤務時間より短くしてください");
  }

  let lateNightBeforeBreak = 0;
  for (let minute = 0; minute < rawElapsed; minute += 1) {
    if (isLateNightMinute(new Date(startedAt.getTime() + minute * 60_000))) {
      lateNightBeforeBreak += 1;
    }
  }
  const normalBeforeBreak = rawElapsed - lateNightBeforeBreak;

  // 休憩時刻を持たないため、通常帯から先に控除し、残りを深夜帯から控除する。
  const normalMinutes = Math.max(0, normalBeforeBreak - shift.breakMinutes);
  const remainingBreak = Math.max(0, shift.breakMinutes - normalBeforeBreak);
  const lateNightMinutes = Math.max(0, lateNightBeforeBreak - remainingBreak);
  const workedMinutes = normalMinutes + lateNightMinutes;

  const configuredRate = config.staff.find((staff) => staff.staffId === shift.staffId)?.hourlyRate;
  const rate = shift.baseHourlyRateSnapshot ?? configuredRate;
  if (!rate || rate <= 0 || !Number.isInteger(rate)) {
    throw new Error("従業員の時給設定がありません");
  }

  const baseWage = roundYen((workedMinutes * rate) / 60, config.wageRoundingMode);
  const lateNightPremium = roundYen(
    (lateNightMinutes * rate * config.lateNightPremiumRate) / 60,
    config.wageRoundingMode,
  );

  return {
    elapsedMinutes: workedMinutes,
    normalMinutes,
    lateNightMinutes,
    baseWage,
    lateNightPremium,
    totalWage: baseWage + lateNightPremium,
  };
}

export function calculateDaily(payload: DailyPayload, config: AppConfig): DailyCalculation {
  const salesTotal = payload.customers.reduce((sum, row) => sum + row.amount, 0);
  const nonCashSales =
    payload.payments.paypay + payload.payments.card + payload.payments.tab + payload.payments.transferDue;
  const cashSales = salesTotal - nonCashSales;
  const expenseTotal = payload.expenses.reduce((sum, row) => sum + row.amount, 0);
  const cashExpenseTotal = payload.expenses
    .filter((row) => row.paymentMethod === "CASH")
    .reduce((sum, row) => sum + row.amount, 0);
  const shifts = payload.shifts.map((shift) => calculateShift(shift, config));
  const wageTotal = shifts.reduce((sum, shift) => sum + shift.totalWage, 0);
  const cashWagePaidTotal = shifts.reduce(
    (sum, shift, index) => sum + (payload.shifts[index]?.paidTodayMethod === "CASH" ? shift.totalWage : 0),
    0,
  );
  const cashCollectionTotal = payload.collections
    .filter((row) => row.receivedMethod === "CASH")
    .reduce((sum, row) => sum + row.amount, 0);
  const dailyProfit = salesTotal - expenseTotal - wageTotal;
  const expectedRegisterCash = payload.openingRegisterCash == null
    ? null
    : payload.openingRegisterCash + cashSales + cashCollectionTotal - cashExpenseTotal - cashWagePaidTotal;

  return {
    salesTotal,
    nonCashSales,
    cashSales,
    expenseTotal,
    cashExpenseTotal,
    wageTotal,
    cashWagePaidTotal,
    cashCollectionTotal,
    dailyProfit,
    expectedRegisterCash,
    registerDifference: payload.closingRegisterCashActual == null || expectedRegisterCash == null
      ? null
      : payload.closingRegisterCashActual - expectedRegisterCash,
    shifts,
  };
}

export function validateDailyIntegrity(
  payload: DailyPayload,
  config: AppConfig,
): { calculation?: DailyCalculation; issues: IntegrityIssue[] } {
  const issues: IntegrityIssue[] = [];
  let calculation: DailyCalculation | undefined;

  try {
    calculation = calculateDaily(payload, config);
  } catch (error) {
    issues.push({
      code: "SHIFT_INVALID",
      message: error instanceof Error ? error.message : "勤務時間を確認してください",
      field: "shifts",
    });
  }

  if (calculation && calculation.cashSales < 0) {
    issues.push({
      code: "PAYMENT_EXCEEDS_SALES",
      message: "現金以外の決済合計が来客利用額を超えています",
      field: "payments",
    });
  }

  const tabDetails = payload.receivables
    .filter((row) => row.type === "TAB")
    .reduce((sum, row) => sum + row.amount, 0);
  if (tabDetails !== payload.payments.tab) {
    issues.push({
      code: "TAB_MISMATCH",
      message: `ツケ決済 ${payload.payments.tab.toLocaleString("ja-JP")}円と未収明細 ${tabDetails.toLocaleString("ja-JP")}円が一致しません`,
      field: "receivables",
    });
  }

  const transferDetails = payload.receivables
    .filter((row) => row.type === "TRANSFER_DUE")
    .reduce((sum, row) => sum + row.amount, 0);
  if (transferDetails !== payload.payments.transferDue) {
    issues.push({
      code: "TRANSFER_DUE_MISMATCH",
      message: `振込予定 ${payload.payments.transferDue.toLocaleString("ja-JP")}円と未収明細 ${transferDetails.toLocaleString("ja-JP")}円が一致しません`,
      field: "receivables",
    });
  }

  const tooLarge = [
    ...payload.customers.map((row) => row.amount),
    ...payload.expenses.map((row) => row.amount),
    ...payload.receivables.map((row) => row.amount),
    ...payload.collections.map((row) => row.amount),
  ].some((amount) => amount > config.maxLineAmount);
  if (tooLarge || (calculation?.salesTotal ?? 0) > config.maxDailyAmount) {
    issues.push({
      code: "DAILY_LIMIT_EXCEEDED",
      message: "設定された金額上限を超えています",
    });
  }

  return { calculation, issues };
}

export const yen = (value: number) =>
  new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY", maximumFractionDigits: 0 }).format(value);

export const minutesLabel = (minutes: number) => `${Math.floor(minutes / 60)}時間${minutes % 60}分`;
